import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { QRCodeSVG } from "qrcode.react";
import logo from "../../assets/cashlet.svg";
import "./invite.css";

const APP_STORE_URL =
  "https://apps.apple.com/us/app/cashlet-savings-investments/id6463010865";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.cashlet";
const ANDROID_PACKAGE = "com.cashlet";
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
const CANONICAL_INVITE_URL = `${SITE_URL}/invite`;

const getDeviceType = () => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  return "desktop";
};

const InvitePage = () => {
  const location = useLocation();
  const [deviceType] = useState(getDeviceType);

  const deepLink = useMemo(
    () => `${window.location.origin}${location.pathname}${location.search}`,
    [location]
  );

  const storeUrl = deviceType === "android" ? PLAY_STORE_URL : APP_STORE_URL;
  const storeName = deviceType === "android" ? "Play Store" : "App Store";

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  const openApp = () => {
    if (deviceType === "android") {
      // Android resolves this directly to the app if installed, and to
      // browser_fallback_url automatically if it isn't - no JS timing needed.
      const query = location.search ? location.search.slice(1) : "";
      const intentUrl = `intent://${window.location.host}${location.pathname
        }${query ? `?${query}` : ""}#Intent;scheme=https;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(
          PLAY_STORE_URL
        )};end`;
      window.location.href = intentUrl;
      return;
    }

    // iOS has no reliable way to detect a failed app-open from JS - retrying
    // the same universal link either opens the app or is a harmless no-op on
    // this same page. The App Store link below stays visible as the fallback.
    window.location.href = deepLink;
  };

  if (deviceType === "desktop") {
    return (
      <div className="invite-page px-4 md:px-8 py-16 md:py-24">
        <div className="max-w-md mx-auto text-center">
          <img
            src={logo}
            alt="Cashlet"
            className="h-8 md:h-10 w-auto mx-auto mb-10"
          />
          <h1 className="landing-text normal-case text-2xl md:text-3xl mb-4">
            Invite friends. Grow your wealth.
          </h1>
          <p className="text-muted text-base mb-2">
            You've made great progress — and you can now invite friends to
            Cashlet.
          </p>
          <p className="text-muted text-base mb-10">
            When someone you invite starts saving and investing with Cashlet,
            your money earns extra growth automatically.
          </p>

          <div className="flex justify-center mb-4">
            <div className="invite-qr-box">
              <QRCodeSVG value={CANONICAL_INVITE_URL} size={180} marginSize={2} />
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-10">
            Scan with your phone to open Cashlet and invite a friend.
          </p>

          <p className="text-sm text-gray-400">
            You can also open the Cashlet app and invite friends from there.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-page px-4 md:px-8 py-16 md:py-24">
      <div className="max-w-md mx-auto text-center">
        <img
          src={logo}
          alt="Cashlet"
          className="h-8 md:h-10 w-auto mx-auto mb-10"
        />
        <h1 className="landing-text normal-case text-2xl md:text-3xl mb-4">
          Invite friends. Grow your wealth.
        </h1>
        <p className="text-muted text-base mb-2">
          You've made great progress — and you can now invite friends to
          Cashlet.
        </p>
        <p className="text-muted text-base mb-2">
          When someone you invite starts saving and investing with Cashlet,
          your money earns extra growth automatically.
        </p>
        <p className="text-muted text-base mb-8">
          Every qualifying referral helps your money earn extra growth over
          time. Start with one friend today.
        </p>

        <button
          type="button"
          onClick={openApp}
          className="btn-primary px-8 py-4 font-semibold text-base w-full"
        >
          Open Cashlet to get started
        </button>
        <p className="text-sm text-gray-500 mt-4">
          It only takes a few seconds to share your link.
        </p>

        <a
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-gray-400 underline mt-6 inline-block"
        >
          Don't have Cashlet? Get it on the {storeName}.
        </a>
      </div>
    </div>
  );
};

export default InvitePage;
